import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { combineLatest, Observable } from 'rxjs';
import { ICON } from '@core/util/icon';
import {
	CostBreakdownComponent,
	FCTTitleInsuranceCostBreakdownData,
} from '../../../fct/components/cost-breakdown/cost-breakdown.component';
import {
	MatterManualTitleInsurance,
	TitleInsuranceDocument,
	TitleInsurancePolicy,
	TitleInsuranceProvider,
} from '@models/title-insurance';
import {
	PdfModalComponent,
	PdfModalData,
} from '@components/pdf-modal/pdf-modal.component';
import { cloneDeep } from 'lodash';
import { StewartFunctions } from '@functions/stewart.service';
import { AuthState } from '@state/auth';
import { take } from 'rxjs/operators';
import { ErrorCodes } from '@fnModels/error-codes';
import LogRocket from 'logrocket';
import { SnackbarService } from 'src/app/shared/services/ui/snackbar.service';
import { getMimeType } from '@core/util/mime';
import {
	TitleInsurancePillData,
	getCounselFeeHover,
	getPremiumPillHover,
} from '@core/util/title-insurance';
import { checkErrorCode } from '@globalUtils/utilities';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { DirectionOfFeeType } from '@models/businessSettings/directionOfFees';
import { StewartOAuthLoginComponent } from '@components/stewart-oauth-login/stewart-oauth-login.component';
import { TestHooks } from './ordered.hooks';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { inject } from '@angular/core';

@UntilDestroy()
@Component({
	selector: 'app-stewart-ordered',
	templateUrl: './ordered.component.html',
	styleUrls: ['./ordered.component.scss'],
	standalone: false,
})
export class StewartOrderedComponent implements OnInit {
	testHooks = TestHooks;
	public ICON = ICON;
	public titleInsurance: MatterManualTitleInsurance;
	public displayPill = 'none';
	public premiumPillData: TitleInsurancePillData;
	public counselPillData: TitleInsurancePillData;

	@Select(SelectedMatter.getTitleInsurance)
	titleInsurance$: Observable<MatterManualTitleInsurance>;
	@Select(SelectedMatter.titleInsuranceAmountDueByClient)
	amountDueByClient$: Observable<number>;
	@Select(SelectedMatter.getTitleInsuranceDirectionOfAmount)
	titleInsuranceDOA$: Observable<DirectionOfFeeType>;
	@Select(SelectedMatter.titleInsurancePolicies) policies$: Observable<
		TitleInsurancePolicy[]
	>;
	@Select(AuthState.getUserId) userId: string;

	drawerService = inject(DrawerService);

	constructor(
		private dialog: MatDialog,
		private stewartFunctions: StewartFunctions,
		private snackbarService: SnackbarService,
		private analytics: AnalyticsService,
	) {}

	ngOnInit() {
		combineLatest([this.titleInsurance$, this.titleInsuranceDOA$])
			.pipe(untilDestroyed(this))
			.subscribe(([_titleInsurance, titleInsuranceDOA]) => {
				if (_titleInsurance) {
					this.titleInsurance = cloneDeep(_titleInsurance);
				}

				this.premiumPillData = getPremiumPillHover(titleInsuranceDOA);
				this.counselPillData = getCounselFeeHover(
					_titleInsurance,
					titleInsuranceDOA,
				);
			});
	}

	policyTypeString(type: string): string {
		if (type === 'lender') {
			return 'Lender policy';
		}

		if (type === 'owner') {
			return 'Homeowner policy';
		}

		return type;
	}

	async openDoc(doc: TitleInsuranceDocument): Promise<void> {
		doc['loading'] = true;
		try {
			LogRocket.log('Fetching Stewart Title Insurance Document', doc.url);
			const document = await this.stewartFunctions
				.getDocument_v2(doc.url)
				.pipe(take(1))
				.toPromise();
			LogRocket.log('Stewart document successfully downloaded');

			const type = doc.name.substring(doc.name.lastIndexOf('.') + 1);

			const data = document;

			const _data: PdfModalData = {
				base64Encoded: {
					data: data.document,
					encoding: data.mimeType ?? getMimeType[type.toLowerCase()],
				},
				fileName: doc.name.replace('.pdf', '').replace('.docx', ''),
			};

			this.openPDF(_data);
		} catch (err) {
			if (checkErrorCode(err?.code, ErrorCodes.UNAUTHENTICATED)) {
				LogRocket.log(
					'Stewart document failed to download, user not authenticated',
					err,
				);
				this.dialog
					.open(StewartOAuthLoginComponent)
					.afterClosed()
					.pipe(take(1))
					.subscribe((result) => {
						if (result) {
							void this.openDoc(doc);
						}
					});
			} else if (
				checkErrorCode(err?.code, ErrorCodes.UNKNOWN) &&
				err?.details?.errors?.length > 0
			) {
				this.snackbarService.openSnackBar({
					text: `Unable to open document: ${err.details.errors[0]?.Message}`,
					type: 'error',
				});
			} else {
				this.snackbarService.openSnackBar({
					text: 'Unable to open document due to unknown error',
					type: 'error',
				});
			}
			LogRocket.error('Stewart - Unable to open document ', err);
		} finally {
			doc['loading'] = false;
		}
	}

	showBreakdown() {
		this.analytics.logEvent(AnalyticsEvent.TITLE_INSURANCE_VIEW_FEE_BREAKDOWN, {
			provider: TitleInsuranceProvider.STEWART,
		});
		const data: FCTTitleInsuranceCostBreakdownData = {
			type: 'ORDER',
		};
		this.dialog.open(CostBreakdownComponent, { data });
	}

	togglePill(_displayPill: string) {
		this.displayPill = _displayPill;
	}

	openPDF(data: PdfModalData) {
		this.drawerService.openDrawer(PdfModalComponent, {
			data,
			title: data.fileName,
			noStyle: true,
		});
	}
}
